import React from 'react';
import PropTypes from 'prop-types';
import lodashGroupBy from 'lodash/fp/groupBy';
import lodashKeyBy from 'lodash/fp/keyBy';

import {
  UTILITY_ACTIVATION_ITEM_STATUS_SETUP_COMPLETED,
  UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED,
  UTILITY_ACTIVATION_ITEM_STATUS_DROPOUT,
} from 'common/utility/constants';

import {
  makeAddressStreetDisplay,
  makeAddressCityStateString,
} from '@utilityprofit/address-helpers';

import SrCard from 'client/components/SrCard/SrCard';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrLink from 'client/components/SrLink/SrLink';
import SrUtilityProfitAddressStatusSummary from 'client/components/SrUtilityProfitAdminProcessAddressPage/SrUtilityProfitAddressStatusSummary';
import SrUtilityProfitAdminProcessAddressForm from 'client/components/SrUtilityProfitAdminProcessAddressPage/SrUtilityProfitAdminProcessAddressForm';

import './SrUtilityProfitAdminProcessAddressPage.less';

const SrUtilityProfitAdminProcessAddressPage = ({
  addressCoverage,
  utilityTypes,
  updateProviderCoveragePoints,
  utilityProvidersInRange = [],
  utilityProvidersByAddress = [],
  toggleRequiredType,
  onMapServiceAreaProvider,
  onConfirmMapping,
}) => {
  const utilityProvidersByTypeId = addressCoverage.items.reduce((memo, item) => {
    const byAddress = utilityProvidersByAddress.filter(
      ({ utilityTypeId, type }) => utilityTypeId === item.type.id || type?.id === item.type.id,
    );
    const mergedById = {
      ...lodashKeyBy('id', byAddress),
      ...lodashKeyBy('id', item.availableProviders || []),
    };

    memo[item.type.id] = Object.values(mergedById);
    return memo;
  }, {});
  const utilityProvidersInRangeByTypeId = lodashGroupBy('utilityTypeId', utilityProvidersInRange);
  const utilityProvidersByAddressByTypeId = lodashGroupBy(
    'utilityTypeId',
    utilityProvidersByAddress,
  );
  return (
    <div className="SrUtilityProfitAdminProcessAddressPage">
      <div styleName="layoutColumns">
        <div>
          <SrBox height="100%" backgroundColor="#FFFFFF">
            <SrBox padding="34px 10px 15px" borderBottom="2px solid #e9ebec">
              <SrText
                font="greycliff"
                weight="800"
                size="16"
                display="block"
                paddingBottom="11px"
                lineHeight="24px"
              >
                <SrLink
                  color="blue300"
                  cursor="pointer"
                  to="/utility-profit/admin/dashboard/mapping-hitlist"
                >
                  {'< Back to Mapping Dashboard'}
                </SrLink>
              </SrText>
              <SrText font="greycliff" weight="800" size="18" display="block" lineHeight="24px">
                {makeAddressStreetDisplay(addressCoverage)}
              </SrText>

              <SrText
                font="greycliff"
                weight="800"
                size="18"
                display="block"
                paddingBottom="11px"
                lineHeight="24px"
              >
                {makeAddressCityStateString(addressCoverage)}
              </SrText>
            </SrBox>

            <SrBox padding="15px 10px" borderBottom="2px solid #e9ebec">
              <SrText font="greycliff" weight="800" size="18" display="block" paddingBottom="12px">
                Admin prep summary
              </SrText>
              <SrUtilityProfitAddressStatusSummary
                utilityTypes={utilityTypes}
                requiredTypes={addressCoverage.requiredTypes}
                items={addressCoverage.items}
                completedStates={[
                  UTILITY_ACTIVATION_ITEM_STATUS_SETUP_COMPLETED,
                  UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED,
                  UTILITY_ACTIVATION_ITEM_STATUS_DROPOUT,
                ]}
              />
            </SrBox>
          </SrBox>
        </div>
        <SrBox overflowY="auto" paddingTop="10px">
          <SrCard noContentPadding maxWidth="calc(100% - 32px)" borderRadius="14px">
            <SrBox padding="0 25px 25px">
              <SrText font="greycliff" size="21" weight="700">
                Fill in available utilities.
              </SrText>
            </SrBox>
            <SrUtilityProfitAdminProcessAddressForm
              addressCoverage={addressCoverage}
              utilityTypes={utilityTypes}
              utilityProvidersByTypeId={utilityProvidersByTypeId}
              updateProviderCoveragePoints={updateProviderCoveragePoints}
              toggleRequiredType={toggleRequiredType}
              utilityProvidersInRangeByTypeId={utilityProvidersInRangeByTypeId}
              utilityProvidersByAddressByTypeId={utilityProvidersByAddressByTypeId}
              onMapServiceAreaProvider={onMapServiceAreaProvider}
              onConfirmMapping={onConfirmMapping}
            />
          </SrCard>
        </SrBox>
      </div>
    </div>
  );
};

SrUtilityProfitAdminProcessAddressPage.propTypes = {
  addressCoverage: PropTypes.object.isRequired,
  utilityTypes: PropTypes.array.isRequired,
  updateProviderCoveragePoints: PropTypes.func.isRequired,
  toggleRequiredType: PropTypes.func.isRequired,
  utilityProvidersInRange: PropTypes.array,
  onConfirmMapping: PropTypes.func.isRequired,
};

SrUtilityProfitAdminProcessAddressPage.displayName = 'SrUtilityProfitAdminProcessAddressPage';

export default SrUtilityProfitAdminProcessAddressPage;
